import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';

import { useGetPropertyContentSources } from '~/hooks/apis/accommodation/usePropertyContentSources';

import CustomContentField, { CUSTOM_CONTENT_FIELDS } from './CustomContentField';

const DERIVED_SOURCE_TYPES = new Set(['custom', 'generated']);

export default function AccommodationPropertyCustomContentSection() {
  const { propertyId } = useParams<{ propertyId: string }>();

  const { data, isLoading, error } = useGetPropertyContentSources({
    propertyId,
    enabled: !!propertyId,
  });

  const fieldsWithData = useMemo(
    () =>
      CUSTOM_CONTENT_FIELDS.map((config) => {
        const fieldSelection = data?.propertyFields?.find((pf) => pf.field === config.field);
        const nonCustomSource = data?.availableSources?.find(
          (source) => source.field === config.field && !DERIVED_SOURCE_TYPES.has(source.type) && source.value,
        );

        return {
          config,
          fieldSelection,
          originalContent: nonCustomSource?.value ?? null,
          originalSourceId: nonCustomSource?.id ?? null,
        };
      }),
    [data?.propertyFields, data?.availableSources],
  );

  if (!propertyId) return null;

  return (
    <Box>
      {isLoading && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress variant="indeterminate" />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error">
          <Typography>Failed to load content sources: {error.message}</Typography>
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Custom Content</Typography>
            <Typography variant="body2" color="text.secondary">
              Set custom content for A/B experiment variants. Fields left empty use the default source.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            {fieldsWithData.map(({ config, fieldSelection, originalContent, originalSourceId }) => (
              <CustomContentField
                key={config.field}
                config={config}
                propertyId={propertyId}
                fieldSelection={fieldSelection}
                originalContent={originalContent}
                originalSourceId={originalSourceId}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
